import type { Vec2 } from './vec';
import { cross, dot, len, sub } from './vec';

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

export function normalizeDeg(deg: number): number {
  const normalized = deg % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

export function angleAt(vertex: Vec2, p: Vec2, q: Vec2): number {
  const vp = sub(p, vertex);
  const vq = sub(q, vertex);

  const vpLen = len(vp);
  const vqLen = len(vq);

  if (vpLen === 0 || vqLen === 0) {
    return 0;
  }

  const dotProduct = dot(vp, vq);
  const normalizedDot = dotProduct / (vpLen * vqLen);
  const clamped = Math.max(-1, Math.min(1, normalizedDot));
  const radians = Math.acos(clamped);
  return radToDeg(radians);
}

export interface TransversalAngleId {
  crossing: 0 | 1;
  sector: 0 | 1 | 2 | 3;
}

export type PairRelation = 'corresponding' | 'alternate' | 'co-interior' | 'vertical' | null;

/**
 * Classify the relationship between two angles formed by a transversal crossing two parallel lines.
 *
 * Geometry:
 * - Crossing 0 is above crossing 1. Interior sectors per crossing: 0→[1,2], 1→[0,3]
 * - Sectors run CCW; 0 and 1 lie on one side of the transversal, 2 and 3 on the other
 *
 * Classification rules:
 * - Same crossing + sectors differ by 2 (mod 4) → 'vertical'
 * - Different crossings + same sector → 'corresponding'
 * - Different crossings + both interior + opposite sides → 'alternate'
 * - Different crossings + both interior + same side → 'co-interior'
 * - Anything else → null
 */
export function classifyTransversalPair(
  a: TransversalAngleId,
  b: TransversalAngleId,
): PairRelation {
  const { crossing: ca, sector: sa } = a;
  const { crossing: cb, sector: sb } = b;

  // Normalize so a is always the "smaller" one for consistency
  const [first, second] = ca < cb || (ca === cb && sa < sb) ? [a, b] : [b, a];

  // Vertical: same crossing, sectors differ by 2 (mod 4)
  if (first.crossing === second.crossing) {
    const diff = Math.abs(first.sector - second.sector);
    if (diff === 2 || diff === -2) {
      return 'vertical';
    }
    return null;
  }

  // Different crossings
  const sameFirst = first.sector === second.sector;

  if (sameFirst) {
    // Corresponding: same sector
    return 'corresponding';
  }

  // Interior sectors: crossing 0 → [1,2], crossing 1 → [0,3]
  const interiorA =
    (first.crossing === 0 && (first.sector === 1 || first.sector === 2)) ||
    (first.crossing === 1 && (first.sector === 0 || first.sector === 3));
  const interiorB =
    (second.crossing === 0 && (second.sector === 1 || second.sector === 2)) ||
    (second.crossing === 1 && (second.sector === 0 || second.sector === 3));

  if (!interiorA || !interiorB) {
    return null;
  }

  // Same side of transversal: (0,1 on left; 2,3 on right)
  const sideA = first.sector <= 1 ? 'left' : 'right';
  const sideB = second.sector <= 1 ? 'left' : 'right';

  if (sideA === sideB) {
    return 'co-interior';
  } else {
    return 'alternate';
  }
}

export function interiorAngleSum(n: number): number {
  return (n - 2) * 180;
}

export function turtleWalkTurns(polygon: readonly Vec2[]): number[] {
  const turns: number[] = [];
  const n = polygon.length;

  if (n < 2) {
    return turns;
  }

  for (let i = 0; i < n; i++) {
    const prev = polygon[(i - 1 + n) % n]!;
    const curr = polygon[i]!;
    const next = polygon[(i + 1) % n]!;

    const edge1 = sub(curr, prev);
    const edge2 = sub(next, curr);

    const c = cross(edge1, edge2);
    const d = dot(edge1, edge2);

    const radians = Math.atan2(c, d);
    const degrees = radToDeg(radians);

    turns.push(degrees);
  }

  return turns;
}
